import assetUtils, { Asset } from 'app-lib/assetUtils'
import providerUtils from 'app-lib/providerUtils'
import appConfig, { ChainId } from 'bzx-common/src/config/appConfig'
import { BigNumber } from '@0x/utils'
import { FulcrumProvider } from '../../../fulcrum/src/services/FulcrumProvider'
import BorrowRequest from '../domain/borrow/BorrowRequest'
import RepayLoanRequest from '../domain/borrow/RepayLoanRequest'
import IBorrowedFundsState from '../domain/IBorrowedFundsState'
import ManageCollateralRequest from '../domain/ManageCollateralRequest'
import { ITradeRequest, PositionType, TradeType } from '../domain/trade.types'
import tradeUtils from '../lib/tradeUtils'

const ZERO_LOAN_ID = '0x0000000000000000000000000000000000000000000000000000000000000000'

export default class LoanService {
  fulcrumProvider: FulcrumProvider

  getDependenciesStrict() {
    const deps = providerUtils.getDependenciesStrict(this.fulcrumProvider)
    const bzxContract = deps.contractsSource.getiBZxContract()
    return { ...deps, bzxContract }
  }

  getDependenciesStrictAsset(loanAsset: Asset) {
    const deps = providerUtils.getDependenciesStrict(this.fulcrumProvider)
    const iTokenContract = deps.contractsSource.getITokenContract(loanAsset)
    return { ...deps, iTokenContract }
  }

  getDependenciesRead() {
    return providerUtils.getDependenciesStrictRead(this.fulcrumProvider)
  }

  /**
   *
   * @param chainId
   * @param borrowDetails
   */
  async borrow(chainId: ChainId, borrowDetails: BorrowRequest) {
    const { borrowAsset, collateralAsset, depositAmount, borrowAmount, loanId } = borrowDetails
    const { userAddress, iTokenContract } = this.getDependenciesStrictAsset(borrowAsset)
    const isNative = assetUtils.isNativeTokenForNetwork(chainId, borrowDetails.collateralAsset)
    const collateralAssetErc20Address = assetUtils.getAddress(chainId, collateralAsset)
    const loanPrecision = assetUtils.getDecimals(chainId, borrowAsset)
    const collateralPrecision = assetUtils.getDecimals(chainId, collateralAsset)
    const borrowAmountInBaseUnits = new BigNumber(
      borrowAmount.multipliedBy(10 ** loanPrecision).toFixed(0, 1)
    )
    const depositAmountInBaseUnits = new BigNumber(
      depositAmount.multipliedBy(10 ** collateralPrecision).toFixed(0, 1)
    )

    const tx = iTokenContract.borrow(
      loanId,
      borrowAmountInBaseUnits,
      appConfig.INITIAL_TORQUE_LOAN_DURATION, // approximately 3 months
      depositAmountInBaseUnits,
      isNative ? appConfig.ZERO_ADDRESS : collateralAssetErc20Address,
      userAddress,
      userAddress,
      '0x'
    )

    return providerUtils.runTx(
      this.fulcrumProvider,
      tx,
      iTokenContract,
      isNative ? depositAmountInBaseUnits : undefined
    )
  }

  /**
   *
   * @param chainId
   * @param manageCollateralDetails
   */
  async collateral(chainId: ChainId, manageCollateralDetails: ManageCollateralRequest) {
    const { bzxContract, userAddress } = this.getDependenciesStrict()
    const { loanId, collateralAsset, collateralAmount, isWithdrawal } = manageCollateralDetails

    const collateralPrecision = assetUtils.getDecimals(chainId, collateralAsset)
    const collateralAmountInBaseUnits = new BigNumber(
      collateralAmount.multipliedBy(10 ** collateralPrecision).toFixed(0, 1)
    )
    const isNative = assetUtils.isNativeTokenForNetwork(
      chainId,
      manageCollateralDetails.collateralAsset
    )

    const tx = isWithdrawal
      ? bzxContract.withdrawCollateral(loanId, userAddress, collateralAmountInBaseUnits)
      : bzxContract.depositCollateral(loanId, collateralAmountInBaseUnits)

    return providerUtils.runTx(
      this.fulcrumProvider,
      tx,
      bzxContract,
      isNative && !isWithdrawal ? collateralAmountInBaseUnits : undefined
    )
  }

  /**
   * Repay a loan.
   *
   * Note:
   * We want to send a bigger number than actually owed, because the amount owed will have
   * increased by the time a TX is done due to interest.
   * The smart contract will use the right max amount from the user's wallet
   * if a bigger repay amount than owed is sent.

   * If you are repaying with ETH, you have to send ETH directly,
   * so in this case the closeAmount param is still that really huge number, but
   * the "value" param of ETH to send is just a larger amount of ETH that should be enough
   * to cover the loan (but not the super huge value of course since their wallet won't have that much)
   * @param chainId
   * @param repayDetails
   */
  async repay(chainId: ChainId, repayDetails: RepayLoanRequest) {
    const { userAddress, bzxContract } = this.getDependenciesStrict()
    const { loanId, borrowAsset, repayAmount } = repayDetails
    const loanPrecision = assetUtils.getDecimals(chainId, borrowAsset)
    let closeAmountInBaseUnits = repayAmount.times(10 ** loanPrecision)
    let closeAmountInBaseUnitsValue = closeAmountInBaseUnits // for native token
    const isNative = assetUtils.isNativeTokenForNetwork(chainId, borrowAsset)

    if (
      repayAmount.gte(repayDetails.amountOwed) ||
      repayDetails.repayPercent.gte(99.9) // assuming the user wanted to close everything
    ) {
      // send a large amount to close entire loan
      closeAmountInBaseUnits = closeAmountInBaseUnits.times(10 ** 50)
      if (closeAmountInBaseUnits.eq(0)) {
        closeAmountInBaseUnits = new BigNumber(10 ** 50)
      }
      closeAmountInBaseUnitsValue = repayAmount
        .times(1.001)
        .dp(loanPrecision)
        .times(10 ** loanPrecision)
    }

    const tx = bzxContract.closeWithDeposit(loanId, userAddress, closeAmountInBaseUnits, '0x')

    return providerUtils.runTx(
      this.fulcrumProvider,
      tx,
      bzxContract,
      isNative ? closeAmountInBaseUnitsValue : undefined
    )
  }

  async getLiquidityFromBlockchain(assets: Asset[]) {
    const { helperContract, chainId } = this.getDependenciesRead()
    const addresses = assets.map((asset) => assetUtils.getITokenAddress(chainId, asset))
    const liquidities = await helperContract.marketLiquidity(addresses).callAsync()
    return assets.reduce((acc, asset, index) => {
      acc[asset.toLowerCase()] = liquidities[index]
        .div(10 ** assetUtils.getDecimals(chainId, asset))
        .toFixed()
      return acc
    }, {} as Record<string, string>)
  }

  /**
   *
   * @param chainId
   * @param tradeDetails
   */
  async tradeClose(chainId: ChainId, tradeDetails: ITradeRequest, loan: IBorrowedFundsState) {
    const isLong = tradeDetails.positionType === PositionType.LONG

    let amountInBaseUnits = new BigNumber(0)

    if (isLong) {
      const decimals = assetUtils.getDecimals(chainId, loan.collateralAsset)
      amountInBaseUnits = new BigNumber(
        tradeDetails.amount.multipliedBy(10 ** decimals).toFixed(0, 1)
      )
    } else {
      const { maxTradeValue } = await tradeUtils.getMaxTradeValue(this.fulcrumProvider, {
        tradeType: TradeType.CLOSE_POSITION,
        baseToken: loan.loanAsset,
        quoteToken: loan.collateralAsset,
        depositToken: tradeDetails.returnTokenIsCollateral ? loan.collateralAsset : loan.loanAsset,
        positionType: tradeDetails.positionType,
        loan,
      })
      amountInBaseUnits = new BigNumber(
        loan.loanData.collateral.times(tradeDetails.amount.div(maxTradeValue)).toFixed(0, 1)
      )
    }

    const maxAmountInBaseUnits = loan.loanData.collateral

    if (maxAmountInBaseUnits.minus(amountInBaseUnits).abs().div(maxAmountInBaseUnits).lte(0.05)) {
      amountInBaseUnits = new BigNumber(maxAmountInBaseUnits.times(10 ** 50).toFixed(0, 1))
    }

    const { bzxContract, userAddress } = this.getDependenciesStrict()

    const tx = bzxContract.closeWithSwap(
      loan.loanId,
      userAddress,
      amountInBaseUnits,
      tradeDetails.returnTokenIsCollateral,
      tradeDetails.loanDataBytes
    )

    return providerUtils.runTx(this.fulcrumProvider, tx, bzxContract)
  }

  /**
   *
   * @param chainId
   * @param tradeDetails
   */
  async tradeOpen(chainId: ChainId, tradeDetails: ITradeRequest) {
    const isLong = tradeDetails.positionType === PositionType.LONG
    const { depositToken } = tradeDetails
    const loanToken = isLong ? tradeDetails.quoteToken : tradeDetails.baseToken
    const decimals = assetUtils.getDecimals(chainId, depositToken)
    const amountInBaseUnits = new BigNumber(
      tradeDetails.amount.multipliedBy(10 ** decimals).toFixed(0, 1)
    )
    const { iTokenContract, userAddress } = this.getDependenciesStrictAsset(loanToken)

    const loanTokenSent = depositToken === loanToken ? amountInBaseUnits : new BigNumber(0)

    const collateralToken = isLong ? tradeDetails.baseToken : tradeDetails.quoteToken
    const collateralTokenSent =
      depositToken === collateralToken ? amountInBaseUnits : new BigNumber(0)

    const collateralTokenAddress = assetUtils.isNativeTokenForNetwork(chainId, collateralToken)
      ? appConfig.ZERO_ADDRESS
      : assetUtils.getAddress(chainId, collateralToken)

    const sendAmountForValue = assetUtils.isNativeTokenForNetwork(chainId, depositToken)
      ? amountInBaseUnits
      : new BigNumber(0)

    const tx = iTokenContract.marginTrade(
      ZERO_LOAN_ID,
      new BigNumber(isLong ? tradeDetails.leverage - 1 : tradeDetails.leverage).times(10 ** 18),
      loanTokenSent,
      collateralTokenSent,
      collateralTokenAddress,
      userAddress,
      tradeDetails.loanDataBytes
    )

    return providerUtils.runTx(this.fulcrumProvider, tx, iTokenContract, sendAmountForValue)
  }

  constructor(provider: FulcrumProvider) {
    this.fulcrumProvider = provider
  }
}
